import { formatMessage } from '@formosa/shared/i18n/formatMessage';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  TRAVEL_IDENTITY_GENERIC_TRAVELER,
  travelMemberProductDisplayName,
} from '../member-identity/memberIdentityPresentation';
import { normalizeCursorDateTime } from '../pagination/cursorDateTime';

export const SOCIAL_NOTIFICATION_TYPES = Object.freeze([
  'SOCIAL_FOLLOW',
  'SOCIAL_POST_LIKE',
  'SOCIAL_POST_COMMENT',
  'SOCIAL_COMMENT_REPLY',
]);

export function asIdString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function isPositiveId(value) {
  return /^[1-9]\d*$/.test(asIdString(value));
}

/** SOCIAL_COMMENT IDs must stay exact strings. Do not coerce with Number. */
function isExactPositiveIntegerString(value) {
  return typeof value === 'string' && /^[1-9]\d*$/.test(value);
}

export function notificationTypeMessageKey(type) {
  const key = String(type || '');
  if (SOCIAL_NOTIFICATION_TYPES.includes(key)) {
    return `travel.socialNotification.types.${key}`;
  }
  return 'travel.socialNotification.types.UNKNOWN';
}

export function resolveNotificationHref(notification) {
  const kind = String(notification?.deepLink?.kind || '');
  const primaryId = asIdString(notification?.deepLink?.primaryId ?? notification?.targetId);
  if (kind === 'MEMBER' && isPositiveId(primaryId)) {
    return `/social/creators/${primaryId}`;
  }
  if (kind === 'SOCIAL_POST' && isPositiveId(primaryId)) {
    return socialPostHref(notification, primaryId);
  }
  if (kind === 'SOCIAL_COMMENT') {
    const postId = notification?.deepLink?.postId;
    const parentId = notification?.deepLink?.primaryId;
    const replyId = notification?.deepLink?.secondaryId;
    if (!isExactPositiveIntegerString(postId)
      || !isExactPositiveIntegerString(parentId)
      || !isExactPositiveIntegerString(replyId)) {
      return '';
    }
    const params = new URLSearchParams();
    params.set('commentId', parentId);
    params.set('replyId', replyId);
    return `/social/posts/${postId}?${params.toString()}`;
  }
  return '';
}

/**
 * Comment query is bound to SOCIAL_POST_COMMENT only.
 * SOCIAL_POST_LIKE stays post-only even if a secondary id is present.
 * Invalid comment identity falls back to the post, never a broken query.
 */
function socialPostHref(notification, primaryId) {
  if (notification?.type !== 'SOCIAL_POST_COMMENT') {
    return `/social/posts/${primaryId}`;
  }
  const exactPostId = notification?.deepLink?.primaryId;
  const commentId = notification?.deepLink?.secondaryId;
  if (!isExactPositiveIntegerString(exactPostId) || !isExactPositiveIntegerString(commentId)) {
    return `/social/posts/${primaryId}`;
  }
  const params = new URLSearchParams();
  params.set('commentId', commentId);
  return `/social/posts/${exactPostId}?${params.toString()}`;
}

export function formatNotificationTime(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  } catch {
    return String(value);
  }
}

export function normalizeSocialNotification(value, t) {
  const notificationId = asIdString(value?.notificationId);
  const type = String(value?.type || '');
  const actor = value?.actor || {};
  const memberId = asIdString(actor.memberId);
  const avatarUrl = resolveMemberMediaUrl(actor.avatarUrl) || null;
  const typeKey = notificationTypeMessageKey(type);
  const someone = typeof t === 'function' ? t('travel.socialNotification.someone') : TRAVEL_IDENTITY_GENERIC_TRAVELER;
  const actorLabel = travelMemberProductDisplayName({
    displayName: actor.displayName,
    username: actor.username,
    nickname: actor.nickname,
  }, { genericFallback: someone });
  let body = '';
  if (typeof t === 'function') {
    body = formatMessage(t(typeKey), { actor: actorLabel });
  } else {
    body = 'Notification';
  }

  const sourceDeepLink = value?.deepLink && typeof value.deepLink === 'object' ? value.deepLink : null;
  const deepLink = sourceDeepLink
    ? {
        kind: String(sourceDeepLink.kind || ''),
        primaryId: asIdString(sourceDeepLink.primaryId),
        secondaryId: sourceDeepLink.secondaryId != null ? asIdString(sourceDeepLink.secondaryId) : null,
        postId: sourceDeepLink.postId != null ? asIdString(sourceDeepLink.postId) : null,
      }
    : null;

  const href = resolveNotificationHref({
    type,
    deepLink: sourceDeepLink,
    targetId: value?.targetId,
  });

  return {
    notificationId,
    type,
    actor: {
      memberId,
      displayName: String(actor.displayName || '').trim() || null,
      avatarUrl,
      label: actorLabel,
    },
    targetType: String(value?.targetType || ''),
    targetId: asIdString(value?.targetId),
    secondaryTargetId: value?.secondaryTargetId != null ? asIdString(value.secondaryTargetId) : null,
    titleSnapshot: value?.titleSnapshot != null ? String(value.titleSnapshot) : null,
    bodySnapshot: value?.bodySnapshot != null ? String(value.bodySnapshot) : null,
    createdAt: normalizeCursorDateTime(value?.createdAt),
    createdAtLabel: formatNotificationTime(normalizeCursorDateTime(value?.createdAt)),
    readAt: value?.readAt || null,
    unread: Boolean(value?.unread),
    body,
    deepLink,
    href,
  };
}

function normalizeNotificationCursor(payload) {
  const createTime = normalizeCursorDateTime(payload?.nextCursorCreateTime);
  const notificationId = asIdString(payload?.nextCursorNotificationId);
  if (!createTime || !isPositiveId(notificationId)) return null;
  return {
    cursorCreateTime: createTime,
    cursorNotificationId: notificationId,
  };
}

export function normalizeSocialNotificationPage(payload, t) {
  const items = Array.isArray(payload?.items)
    ? payload.items
        .map((item) => normalizeSocialNotification(item, t))
        .filter((item) => isPositiveId(item.notificationId))
    : [];
  const nextCursor = normalizeNotificationCursor(payload);
  return {
    items,
    hasMore: Boolean(payload?.hasMore) && nextCursor != null,
    nextCursor,
  };
}

/**
 * Badge presentation for visible unread contract.
 * @returns {{ mode: 'hidden'|'exact'|'capped'|'truncated'|'dot', text: string, count: number, capped: boolean, scanTruncated: boolean }}
 */
export function normalizeSocialNotificationUnreadCount(payload) {
  const raw = payload?.unreadCount;
  const count = Number.isFinite(Number(raw)) ? Math.max(0, Math.floor(Number(raw))) : 0;
  const capped = Boolean(payload?.capped);
  const scanTruncated = Boolean(payload?.scanTruncated);

  if (capped) {
    return { mode: 'capped', text: '99+', count, capped: true, scanTruncated };
  }
  if (scanTruncated && count > 0) {
    return { mode: 'truncated', text: `${count}+`, count, capped: false, scanTruncated: true };
  }
  if (scanTruncated && count === 0) {
    return { mode: 'dot', text: '', count: 0, capped: false, scanTruncated: true };
  }
  if (count > 0) {
    return { mode: 'exact', text: String(count), count, capped: false, scanTruncated: false };
  }
  return { mode: 'hidden', text: '', count: 0, capped: false, scanTruncated: false };
}

export function badgeAriaLabel(badge, t) {
  if (!badge || badge.mode === 'hidden') {
    return typeof t === 'function' ? t('travel.socialNotification.bell') : 'Notifications';
  }
  if (badge.mode === 'dot') {
    return typeof t === 'function' ? t('travel.socialNotification.badge.hasUnread') : 'You have unread notifications';
  }
  if (badge.mode === 'capped') {
    return typeof t === 'function'
      ? formatMessage(t('travel.socialNotification.badge.atLeast'), { count: 99 })
      : 'At least 99 unread notifications';
  }
  if (badge.mode === 'truncated') {
    return typeof t === 'function'
      ? formatMessage(t('travel.socialNotification.badge.atLeast'), { count: badge.count })
      : `At least ${badge.count} unread notifications`;
  }
  return typeof t === 'function'
    ? formatMessage(t('travel.socialNotification.badge.count'), { count: badge.count })
    : `${badge.count} unread notifications`;
}

export function mergeSocialNotification(list, notificationId, patch) {
  return (Array.isArray(list) ? list : []).map((item) => (
    String(item.notificationId) === String(notificationId) ? { ...item, ...patch } : item
  ));
}

export function appendUniqueNotifications(current, nextItems) {
  const seen = new Set((Array.isArray(current) ? current : []).map((item) => String(item.notificationId)));
  const appended = (Array.isArray(nextItems) ? nextItems : []).filter((item) => {
    const id = String(item.notificationId);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  return [...(Array.isArray(current) ? current : []), ...appended];
}

export function markAllNotificationsRead(list) {
  return (Array.isArray(list) ? list : []).map((item) => (
    item.unread ? { ...item, unread: false, readAt: item.readAt || new Date().toISOString() } : item
  ));
}
